/**
 * Admin — Template Library.
 *
 * The review surface for hand-authored templates (frontend/src/templateLibrary).
 * Every card is a live render of the real editor document via the app's own
 * PageThumbnailPreview, so what is shown here is exactly what the editor and
 * the export will produce — nothing is drawn by hand for this page.
 *
 * Nothing here touches the database until "Sync to Review Queue" is pressed.
 * That means a design can be looked at, rejected, corrected in code and looked
 * at again without ever leaving a record behind.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import PageThumbnailPreview from '../../components/Editor/PageThumbnailPreview';
import { useToast } from '../../hooks/use-toast';
import {
  Library, PenSquare, Upload, Loader2, X, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { ALL_TEMPLATES, groupedTemplates, buildTemplateDoc, toEditorTemplate } from '../../templateLibrary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Build once per mount — `build()` is pure, but it is not free. */
const useBuiltTemplates = () =>
  useMemo(
    () =>
      ALL_TEMPLATES.map((tpl) => {
        try {
          return { tpl, built: tpl.build(), error: null };
        } catch (e) {
          return { tpl, built: null, error: e.message };
        }
      }),
    [],
  );

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-gray-50 border border-gray-100 text-gray-600 px-2 py-0.5 text-[11px]">
    {children}
  </span>
);

/** A failed build shows as a card, not as a blank page or a crash. */
const BrokenCard = ({ tpl, error }) => (
  <div className="bg-white rounded-2xl border border-red-200 overflow-hidden">
    <div className="aspect-[8.5/11] bg-red-50 flex items-center justify-center p-6">
      <AlertTriangle className="w-8 h-8 text-red-400" />
    </div>
    <div className="p-4">
      <h3 className="text-sm font-semibold text-gray-900">{tpl.meta.name}</h3>
      <p className="text-xs text-red-600 mt-1 break-words">{error}</p>
    </div>
  </div>
);

const TemplateCard = ({ tpl, built, syncState, onPreview, onOpenEditor }) => {
  const page = built.pages[0];
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col">
      <button
        type="button"
        onClick={() => onPreview({ tpl, built })}
        className="relative block w-full bg-gray-100 cursor-zoom-in"
        style={{ aspectRatio: `${built.canvas.width} / ${built.canvas.height}` }}
        aria-label={`Preview ${tpl.meta.name}`}
      >
        <PageThumbnailPreview page={page} canvasSize={built.canvas} fillWidth />
        {syncState && (
          <span className={`absolute top-2 right-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
            syncState.ok ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {syncState.ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            {syncState.ok ? syncState.action : 'failed'}
          </span>
        )}
      </button>

      <div className="p-4 flex-1 flex flex-col">
        <h3 className="text-sm font-semibold text-gray-900">{tpl.meta.name}</h3>
        <p className="text-xs text-gray-500 mt-1 flex-1">{tpl.meta.description}</p>
        <div className="flex flex-wrap gap-1 mt-3 mb-3">
          <Chip>{built.canvas.width}×{built.canvas.height}</Chip>
          <Chip>{tpl.meta.palette}</Chip>
          <Chip>{page.elements.length} elements</Chip>
        </div>
        <Button variant="outline" size="sm" onClick={() => onOpenEditor(tpl)}>
          <PenSquare className="w-3.5 h-3.5 mr-2" /> Open in editor
        </Button>
      </div>
    </div>
  );
};

const PreviewModal = ({ entry, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={onClose}>
    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">{entry.tpl.meta.name}</h3>
        <button onClick={onClose} aria-label="Close preview"><X className="w-4 h-4 text-gray-400" /></button>
      </div>
      <div className="p-5">
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <PageThumbnailPreview page={entry.built.pages[0]} canvasSize={entry.built.canvas} fillWidth />
        </div>
      </div>
    </div>
  </div>
);

const AdminTemplateLibrary = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const entries = useBuiltTemplates();
  const groups = useMemo(() => groupedTemplates(), []);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState({});
  const [previewEntry, setPreviewEntry] = useState(null);

  const buildable = entries.filter((e) => e.built);

  const openInEditor = (tpl) => {
    navigate('/editor', { state: { template: toEditorTemplate(tpl) } });
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/content/templates/import-library`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({ templates: buildable.map(({ tpl }) => buildTemplateDoc(tpl)) }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setSyncResults(Object.fromEntries(data.results.map((r) => [r.library_id, r])));
      toast({
        title: `${data.imported} synced${data.failed ? `, ${data.failed} failed` : ''}`,
        // Library templates sync with source "ai_generated" (LibraryTemplateDoc's
        // default), and Template Review now opens on Imported — so the filter is
        // part of the instruction, not an afterthought.
        description: 'Review and publish them under Moderation → Template Review, filtered to AI Generated.',
        variant: data.failed ? 'destructive' : undefined,
      });
    } catch (err) {
      toast({ title: 'Sync failed', description: err.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Library className="w-6 h-6 text-purple-600" /> Template Library
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Hand-authored templates, rendered live. Nothing is saved until you sync — syncing
              upserts by library id, so re-syncing a corrected design updates it in place.
            </p>
          </div>
          <Button className="bg-purple-600 hover:bg-purple-700" onClick={syncAll} disabled={syncing || buildable.length === 0}>
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Sync to Review Queue ({buildable.length})
          </Button>
        </div>

        {groups.map((group) => (
          <section key={group.label} className="mb-10">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              {group.label} <span className="text-gray-300">· {group.templates.length}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {group.templates.map((tpl) => {
                const entry = entries.find((e) => e.tpl.id === tpl.id);
                if (!entry?.built) return <BrokenCard key={tpl.id} tpl={tpl} error={entry?.error || 'Build failed'} />;
                return (
                  <TemplateCard
                    key={tpl.id}
                    tpl={tpl}
                    built={entry.built}
                    syncState={syncResults[tpl.id]}
                    onPreview={setPreviewEntry}
                    onOpenEditor={openInEditor}
                  />
                );
              })}
            </div>
          </section>
        ))}

        {previewEntry && <PreviewModal entry={previewEntry} onClose={() => setPreviewEntry(null)} />}
      </div>
    </AdminLayout>
  );
};

export default AdminTemplateLibrary;
